<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import OverrideCard from './OverrideCard.vue';
import CreateOverrideModal from './CreateOverrideModal.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';

export default {
  name: 'OperatingHoursOverrides',
  props: {
    selectedBranch: {
      type: Object,
      required: true,
    },
  },
  components: {
    LoadingState,
    EmptyState,
    OverrideCard,
    CreateOverrideModal,
    DeleteModal,
  },
  data() {
    return {
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      editingOverride: null,
      deleteId: null,
      deleteName: null,
      isDeleting: false,
    };
  },
  computed: {
    ...mapGetters('operatingHours', {
      overrides: 'allOverrides',
      isLoading: 'isOverridesLoading',
    }),
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    branchId() {
      return this.selectedBranch?.branch_id || this.selectedBranch?.id;
    },
    hasValidIds() {
      return !!(this.accountId && this.branchId);
    },
  },
  methods: {
    async fetchOverrides() {
      if (!this.hasValidIds) {
        console.warn('Missing accountId or branchId');
        return;
      }

      try {
        await this.$store.dispatch('operatingHours/fetchOverrides', {
          accountId: this.accountId,
          branchId: this.branchId,
        });
      } catch (error) {
        console.error('Error loading overrides:', error);
        useAlert('Failed to load overrides');
      }
    },
    handleCreate() {
      this.showCreateModal = true;
    },
    handleEdit(override) {
      this.editingOverride = override;
      this.showEditModal = true;
    },
    handleCloseCreate() {
      this.showCreateModal = false;
    },
    handleCloseEdit() {
      this.showEditModal = false;
      this.editingOverride = null;
    },
    handleOpenDelete({ id, name }) {
      this.showDeleteModal = true;
      this.deleteId = id;
      this.deleteName = name;
    },
    handleCloseDelete() {
      this.showDeleteModal = false;
      this.deleteId = null;
      this.deleteName = null;
    },
    async handleConfirmDelete() {
      try {
        this.isDeleting = true;
        await this.$store.dispatch('operatingHours/deleteOverride', {
          accountId: this.accountId,
          branchId: this.branchId,
          overrideId: this.deleteId,
        });
        useAlert('Override deleted successfully');
        await this.fetchOverrides();
        this.handleCloseDelete();
      } catch (error) {
        console.error('Error deleting override:', error);
        useAlert(error.message || 'Failed to delete override');
      } finally {
        this.isDeleting = false;
      }
    },
    async refreshPage() {
      await this.fetchOverrides();
    },
  },
  watch: {
    branchId: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.accountId) {
          this.fetchOverrides();
        }
      },
    },
  },
  mounted() {
    // Ensure we fetch if watch didn't trigger
    if (this.hasValidIds) {
      this.fetchOverrides();
    }
  },
};
</script>

<template>
  <div class="mt-8 flex flex-col p-4 bg-white dark:bg-slate-700 rounded-md border-2 border-slate-100 dark:border-slate-50">
    <!-- Modals -->
    <CreateOverrideModal
      v-if="showCreateModal"
      :account-id="accountId"
      :branch-id="branchId"
      @close="handleCloseCreate"
      @refresh="refreshPage"
    />
    <CreateOverrideModal
      v-if="showEditModal"
      :account-id="accountId"
      :branch-id="branchId"
      :is-edit="true"
      :override="editingOverride"
      @close="handleCloseEdit"
      @refresh="refreshPage"
    />
    <DeleteModal
      v-if="showDeleteModal"
      :delete-name="deleteName"
      :is-loading="isDeleting"
      @confirm="handleConfirmDelete"
      @close="handleCloseDelete"
    />

    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Schedule Overrides
      </h2>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        icon="add"
        @click="handleCreate"
      >
        Add Override
      </woot-button>
    </div>

    <!-- Loading State -->
    <LoadingState v-if="isLoading" message="Loading overrides..." />

    <!-- Empty State -->
    <EmptyState
      v-else-if="!isLoading && overrides.length === 0"
      title="No Overrides Found"
      message="Create a new override for special dates or closures"
    />

    <!-- Override List -->
    <div v-else>
      <OverrideCard
        v-for="item in overrides"
        :key="item.id"
        :override="item"
        @edit="handleEdit"
        @delete="handleOpenDelete"
      />
    </div>
  </div>
</template>
